import TimePresets from '../components/quick/TimePresets';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Select from 'react-select';
import { BsPlusCircle, BsXCircle, BsEye, BsThreeDotsVertical } from 'react-icons/bs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';
import TaskAttachments from '../components/TaskAttachments';

const importanceOptions = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Media' },
  { value: 'baja', label: 'Baja' }
];

const estadoOptions = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'realizada', label: 'Realizada' }
];

const badgeColor = { alta: 'danger', media: 'warning', baja: 'secondary' };
const canAssignProject = (project, clientId) => ['a_la_espera', 'en_proceso'].includes(project.estado)
  && (!clientId || String(project.client_id) === String(clientId));

const TasksPage = () => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsReady, setProjectsReady] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [userFilter, setUserFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [autoOpenProjectId, setAutoOpenProjectId] = useState(null);
  const [form, setForm] = useState({
    titulo: '',
    client_id: null,
    importance: null,
    user_id: null,
    estado: 'pendiente',
    comentarios: '',
    workload: null,
    add_to_erp: false,
    project_id: null
  });
  const [defaultUser, setDefaultUser] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [defaultProjectId, setDefaultProjectId] = useState(null);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const fetchTasks = async (params = {}) => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (params.user_id) qs.append('user_id', params.user_id);
      if (params.project_id) qs.append('project_id', params.project_id);
      qs.append('estado', 'pendiente');
      const res = await apiFetch(`/api/tasks${qs.toString() ? `?${qs.toString()}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando tareas');
      const items = Array.isArray(data) ? data : data.items || [];
      setTasks(items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await apiFetch('/api/clients?page=1&pageSize=200', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && data) {
        const list = data.data || data.items || [];
        const clientId = new URLSearchParams(location.search).get('clientId');
        if (clientId && !list.some((client) => String(client.id) === clientId)) {
          const clientRes = await apiFetch(`/api/clients/${encodeURIComponent(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
          const client = await parseJsonSafe(clientRes);
          if (clientRes.ok) list.push(client);
        }
        setClients(list);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) {
        setUsers(data);
        const sel = data.find((u) => u.id === defaultUser);
        if (sel) {
          setForm((p) => ({ ...p, user_id: sel.id }));
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProjects = async () => {
    setProjectsLoading(true);
    setProjectsError('');
    try {
      const all = [];
      let page = 1;
      let total;
      do {
        const res = await apiFetch(`/api/projects?page=${page}&pageSize=500`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'No se pudieron cargar los proyectos');
        const items = Array.isArray(data) ? data : data.items || [];
        all.push(...items);
        total = Number(data.total) || all.length;
        if (!items.length) break;
        page += 1;
      } while (all.length < total);
      setProjects(all);
      setProjectsReady(true);
    } catch (err) {
      setProjectsError(err.message || 'No se pudieron cargar los proyectos');
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchDefaultUser = async () => {
    try {
      const res = await apiFetch('/api/tasks/config/default-user', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setDefaultUser(data.default_user_id || null);
        if (data.default_user_id) {
          setForm((p) => ({ ...p, user_id: data.default_user_id }));
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const projectParam = params.get('projectId') || params.get('project_id') || null;
    const createParam = params.get('create');
    if (projectParam && !Number.isNaN(Number(projectParam))) {
      const parsed = Number(projectParam);
      setDefaultProjectId(parsed);
      setAutoOpenProjectId(createParam === '1' ? parsed : null);
    } else {
      setDefaultProjectId(null);
      setAutoOpenProjectId(null);
    }
  }, [location.search]);

  useEffect(() => {
    fetchTasks({ user_id: userFilter, project_id: projectFilter || defaultProjectId });
    fetchClients();
    fetchDefaultUser().then(fetchUsers);
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, projectFilter, defaultProjectId]);

  useEffect(() => {
    if (defaultProjectId && !projectFilter) {
      setProjectFilter(defaultProjectId);
    }
  }, [defaultProjectId, projectFilter]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openModal = useCallback((task = null, projectIdOverride = null) => {
    setError('');
    setEditing(task);
    setForm({
      titulo: task?.titulo || '',
      client_id: task?.client_id || Number(new URLSearchParams(location.search).get('clientId')) || null,
      importance: task?.importance || null,
      user_id: task?.user_id || defaultUser || null,
      estado: task?.estado || 'pendiente',
      comentarios: task?.comentarios || '',
      workload: task?.workload || null,
      add_to_erp: Boolean(task?.add_to_erp),
      project_id: task?.project_id || projectIdOverride || defaultProjectId || null
    });
    setModalOpen(true);
  }, [defaultProjectId, defaultUser, location.search]);

  useEffect(() => {
    if (!autoOpenProjectId || modalOpen) return;
    openModal(null, autoOpenProjectId);
    setAutoOpenProjectId(null);
    const params = new URLSearchParams(location.search || '');
    params.delete('create');
    const query = params.toString();
    navigate(`/tareas${query ? `?${query}` : ''}`, { replace: true });
  }, [autoOpenProjectId, location.search, modalOpen, navigate, openModal]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('taskId');
    const createForClient = params.get('create') === '1' && params.get('clientId');
    if (!taskId && !createForClient) return;
    let active = true;
    const consume = () => {
      params.delete('taskId'); params.delete('create');
      navigate(`/tareas${params.toString() ? `?${params}` : ''}`, { replace: true });
    };
    if (createForClient) { openModal(); consume(); }
    else {
      (async () => {
        try {
          const res = await apiFetch(`/api/tasks?task_id=${encodeURIComponent(taskId)}`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await parseJsonSafe(res);
          if (!active) return;
          if (res.status === 401) return handleUnauthorized();
          if (!res.ok) throw new Error(data.message || 'No se pudo abrir la tarea');
          const task = data.items?.[0];
          if (!task) throw new Error('Tarea no disponible');
          openModal(task); consume();
        } catch (err) { if (active) { setError(err.message); consume(); } }
      })();
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, openModal, navigate, token]);

  const saveTask = async (e) => {
    e.preventDefault();
    if (!form.titulo || !form.importance) {
      setError('Título e importancia son obligatorios');
      return;
    }
    const keepingExistingProject = editing && String(form.project_id) === String(editing.project_id)
      && String(form.client_id) === String(editing.client_id);
    if (form.project_id && !keepingExistingProject && (!projectsReady || projectsLoading || projectsError
      || !projects.some((project) => String(project.id) === String(form.project_id) && canAssignProject(project, form.client_id)))) {
      setError('Selecciona un proyecto a la espera o en proceso del cliente elegido.');
      return;
    }
    try {
      const payload = { ...form };
      const url = editing ? `/api/tasks/${editing.id}` : '/api/tasks';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la tarea');
      setModalOpen(false);
      setEditing(null);
      fetchTasks({ user_id: userFilter, project_id: projectFilter || defaultProjectId });
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      const res = await apiFetch(`/api/tasks/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      fetchTasks({ user_id: userFilter, project_id: projectFilter || defaultProjectId });
    } catch (err) {
      alert(err.message);
    }
  };

  const clientOptions = clients.map((c) => ({ value: c.id, label: c.razon_social || c.email || `Cliente ${c.id}` }));
  const userOptions = users.map((u) => ({ value: u.id, label: u.name || u.email }));
  const workloadOptions = Array.from({ length: 8 }).map((_, i) => ({ value: i + 1, label: String(i + 1) }));
  const projectOptions = projects.filter((project) => canAssignProject(project, form.client_id))
    .map((project) => ({ value: project.id, label: project.titulo }));
  // An existing task can keep its historic association when only its text changes.
  // Completed projects are never offered as a new selection.
  const selectedProject = projectOptions.find((option) => String(option.value) === String(form.project_id))
    || (editing?.project_id && String(form.project_id) === String(editing.project_id)
      ? { value: editing.project_id, label: editing.project_title || projects.find((project) => String(project.id) === String(editing.project_id))?.titulo || `Proyecto #${editing.project_id}` }
      : null);
  useEffect(() => {
    if (!modalOpen || editing || !projectsReady || projectsLoading || projectsError || !form.project_id) return;
    if (!projects.some((project) => String(project.id) === String(form.project_id) && canAssignProject(project, form.client_id))) {
      setForm((previous) => ({ ...previous, project_id: null }));
    }
  }, [modalOpen, editing, projectsReady, projectsLoading, projectsError, projects, form.project_id, form.client_id]);
  const importanceOrder = useMemo(() => ({ alta: 0, media: 1, baja: 2 }), []);
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => (importanceOrder[a.importance] ?? 3) - (importanceOrder[b.importance] ?? 3));
  }, [importanceOrder, tasks]);

  const [noticeTask, setNoticeTask] = useState(null);
  const [noticeText, setNoticeText] = useState('');
  const [timeModal, setTimeModal] = useState({ open: false, task: null, hours: 1, minutes: 0, comments: '', user_id: null, date: new Date().toISOString().slice(0, 10) });
  const [commentModal, setCommentModal] = useState({ open: false, text: '' });
  const [savingTime, setSavingTime] = useState(false);

  const handleMarkDone = (task) => {
    if (task.client_id) {
      setNoticeTask(task);
      setNoticeText(`Hola,\nLa tarea: ${task.titulo} ha sido realizada.`);
    } else {
      updateTaskStatus(task, 'realizada');
    }
  };

  const updateTaskStatus = async (task, estado) => {
    try {
      const res = await apiFetch(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ estado })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      fetchTasks({ user_id: userFilter, project_id: projectFilter || defaultProjectId });
    } catch (err) {
      alert(err.message);
    }
  };

  const loadHistory = async () => {
    try {
      const qs = new URLSearchParams();
      qs.append('estado', 'realizada');
      if (userFilter) qs.append('user_id', userFilter);
      if (projectFilter) qs.append('project_id', projectFilter);
      const res = await apiFetch(`/api/tasks?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      const items = Array.isArray(data) ? data : data.items || [];
      setHistoryItems(items);
      setHistoryOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Tareas</h2>
        {isMobile ? (
          <div className="position-relative">
            <button className="btn btn-outline-secondary" onClick={() => setActionsMenuOpen((o) => !o)}>
              <BsThreeDotsVertical />
            </button>
            {actionsMenuOpen && (
              <div
                className="dropdown-menu dropdown-menu-end show"
                style={{ minWidth: '230px', width: 'calc(100vw - 32px)', right: 0, left: 'auto' }}
              >
                <button className="dropdown-item" onClick={() => { window.location.href = '/tareas/tiempos'; setActionsMenuOpen(false); }}>Tiempos dedicados</button>
                <button className="dropdown-item" onClick={() => { setShowConfig(true); setActionsMenuOpen(false); }}>Configuración</button>
                <button className="dropdown-item" onClick={() => { loadHistory(); setActionsMenuOpen(false); }}>Historial</button>
                <div className="px-3 py-2">
                  <Select
                    classNamePrefix="select"
                    placeholder="Filtrar por usuario"
                    isClearable
                    options={users.map((u) => ({ value: u.id, label: u.name || u.email }))}
                    value={users.map((u) => ({ value: u.id, label: u.name || u.email })).find((o) => o.value === userFilter) || null}
                    onChange={(opt) => setUserFilter(opt ? opt.value : null)}
                  />
                </div>
                <div className="px-3 pb-2">
                  <Select
                    classNamePrefix="select"
                    placeholder="Filtrar por proyecto"
                    isClearable
                    options={projects.map((p) => ({ value: p.id, label: p.titulo }))}
                    value={projects.map((p) => ({ value: p.id, label: p.titulo })).find((o) => o.value === projectFilter) || null}
                    onChange={(opt) => setProjectFilter(opt ? opt.value : null)}
                  />
                </div>
                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { openModal(); setActionsMenuOpen(false); }}>
                  <BsPlusCircle /> Crear tarea
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-outline-secondary" onClick={() => window.location.href = '/tareas/tiempos'}>Tiempos dedicados</button>
            <button className="btn btn-outline-secondary" onClick={() => setShowConfig(true)}>⚙️ Configuración</button>
            <button className="btn btn-outline-secondary" onClick={loadHistory}>Historial</button>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Filtrar por usuario"
              isClearable
              options={users.map((u) => ({ value: u.id, label: u.name || u.email }))}
              value={users.map((u) => ({ value: u.id, label: u.name || u.email })).find((o) => o.value === userFilter) || null}
              onChange={(opt) => setUserFilter(opt ? opt.value : null)}
              styles={{ container: (p) => ({ ...p, minWidth: 220 }) }}
            />
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Filtrar por proyecto"
              isClearable
              options={projects.map((p) => ({ value: p.id, label: p.titulo }))}
              value={projects.map((p) => ({ value: p.id, label: p.titulo })).find((o) => o.value === projectFilter) || null}
              onChange={(opt) => setProjectFilter(opt ? opt.value : null)}
              styles={{ container: (p) => ({ ...p, minWidth: 220 }) }}
            />
            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={() => openModal()}>
              <BsPlusCircle /> Crear tarea
            </button>
          </div>
        )}
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <p>Cargando...</p>
      ) : (
        <div className="row g-3">
          {sortedTasks.length ? sortedTasks.map((t) => (
            <div key={t.id} className="col-md-4">
              <div className="card shadow-sm h-100" style={{ borderTop: `4px solid ${t.importance === 'alta' ? '#dc3545' : t.importance === 'media' ? '#fd7e14' : '#ffc107'}` }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`badge bg-${badgeColor[t.importance] || 'secondary'} rounded-circle`} style={{ width: 12, height: 12 }}></span>
                      <h5 className="card-title mb-0">{t.titulo}</h5>
                    </div>
                    <button className="btn btn-sm btn-light text-danger" onClick={() => deleteTask(t.id)}>
                      <BsXCircle />
                    </button>
                  </div>
                  <div className="text-muted small mt-1">
                    {t.client_id ? (
                      <Link to={`/clientes/${t.client_id}`} className="text-muted text-decoration-none">
                        {t.client_name || `Cliente #${t.client_id}`}
                      </Link>
                    ) : (
                      'Sin cliente'
                    )}
                  </div>
                  <div className="small">Importancia: {t.importance}</div>
                  <div className="small">Estado: {t.estado}</div>
                  {t.project_title && <div className="small">Proyecto: {t.project_title}</div>}
                  <div className="small text-muted">Asignada a: {t.user_name || t.user_email || 'Sin asignar'}</div>
                  {t.comentarios && (
                    <button
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={() => setCommentModal({ open: true, text: t.comentarios || '' })}
                      title="Ver comentarios"
                    >
                      <BsEye className="me-1" /> Comentarios
                    </button>
                  )}
                  <div className="d-flex gap-2 mt-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openModal(t)}>Editar</button>
                    <button className="btn btn-sm btn-outline-success" onClick={() => handleMarkDone(t)}>Realizada</button>
                    <div className="ms-auto">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setTimeModal({ open: true, task: t, hours: 1, minutes: 0, comments: '', user_id: t.user_id || defaultUser || null, date: new Date().toISOString().slice(0, 10) })}>
                        Tiempo dedicado
                      </button>
                    </div>
                  </div>
                  <TaskAttachments
                    taskId={t.id}
                    attachments={t.attachments}
                    token={token}
                    onChanged={() => fetchTasks({ user_id: userFilter, project_id: projectFilter || defaultProjectId })}
                    onUnauthorized={handleUnauthorized}
                  />
                </div>
              </div>
            </div>
          )) : <p>No hay tareas.</p>}
        </div>
      )}

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Editar tarea' : 'Crear tarea'}</h5>
                <button className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={saveTask}>
                <div className="modal-body">
                  {error && <div className="alert alert-danger" role="alert">{error}</div>}
                  <div className="mb-2">
                    <label className="form-label">Título *</label>
                    <input className="form-control" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-client">Cliente</label>
                  <Select
                    inputId="task-client"
                    options={clientOptions}
                    value={clientOptions.find((o) => String(o.value) === String(form.client_id)) || null}
                    onChange={(opt) => setForm((p) => ({ ...p, client_id: opt ? opt.value : null,
                      project_id: String(p.client_id || '') === String(opt?.value || '') ? p.project_id : null }))}
                    isClearable
                    placeholder="Selecciona cliente"
                    onInputChange={(val) => {
                      if (val && val.length >= 2) {
                        apiFetch(`/api/clients?page=1&pageSize=50&search=${encodeURIComponent(val)}`, { headers: { Authorization: `Bearer ${token}` } })
                          .then((res) => parseJsonSafe(res).then((data) => ({ res, data })))
                          .then(({ res, data }) => {
                            if (res.ok && data) setClients(data.data || data.items || []);
                          })
                          .catch(() => {});
                      }
                    }}
                  />
                  </div>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-project">Proyecto</label>
                    <Select
                      inputId="task-project"
                      options={projectOptions}
                      value={selectedProject}
                      onChange={(opt) => setForm((p) => ({ ...p, project_id: opt ? opt.value : null }))}
                      isClearable
                      isLoading={projectsLoading}
                      isDisabled={projectsLoading || Boolean(projectsError)}
                      placeholder="Asigna a proyecto"
                      noOptionsMessage={() => form.client_id ? 'Este cliente no tiene proyectos a la espera o en proceso' : 'No hay proyectos a la espera o en proceso'}
                    />
                    {projectsError && <div className="text-danger small mt-1" role="alert">{projectsError} <button type="button" className="btn btn-link btn-sm p-0" onClick={fetchProjects}>Reintentar</button></div>}
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Importancia *</label>
                    <Select
                      options={importanceOptions}
                      value={importanceOptions.find((o) => o.value === form.importance) || null}
                      onChange={(opt) => setForm((p) => ({ ...p, importance: opt ? opt.value : null }))}
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Usuario</label>
                    <Select
                      options={userOptions}
                      value={userOptions.find((o) => o.value === form.user_id) || null}
                      onChange={(opt) => setForm((p) => ({ ...p, user_id: opt ? opt.value : null }))}
                      isClearable
                      placeholder="Asignar a usuario"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Estado</label>
                    <Select
                      options={estadoOptions}
                      value={estadoOptions.find((o) => o.value === form.estado) || estadoOptions[0]}
                      onChange={(opt) => setForm((p) => ({ ...p, estado: opt ? opt.value : 'pendiente' }))}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Comentarios</label>
                    <textarea className="form-control" rows="3" value={form.comentarios} onChange={(e) => setForm((p) => ({ ...p, comentarios: e.target.value }))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Carga de trabajo</label>
                    <Select
                      options={workloadOptions}
                      value={workloadOptions.find((o) => o.value === form.workload) || null}
                      onChange={(opt) => setForm((p) => ({ ...p, workload: opt ? opt.value : null }))}
                      isClearable
                      placeholder="Selecciona 1-8"
                    />
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="addToErp" checked={form.add_to_erp} onChange={(e) => setForm((p) => ({ ...p, add_to_erp: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="addToErp">Añadir a ERP</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cerrar</button>
                  <button type="submit" className="btn btn-primary" disabled={Boolean(form.project_id) && projectsLoading}>Guardar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showConfig && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración de tareas</h5>
                <button className="btn-close" onClick={() => setShowConfig(false)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Usuario por defecto al crear tarea</label>
                <Select
                  options={userOptions}
                  value={userOptions.find((o) => o.value === defaultUser) || null}
                  onChange={(opt) => setDefaultUser(opt ? opt.value : null)}
                  isClearable
                  placeholder="Selecciona usuario"
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowConfig(false)}>Cerrar</button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    const res = await apiFetch('/api/tasks/config/default-user', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ default_user_id: defaultUser })
                    });
                    const data = await parseJsonSafe(res);
                    if (res.status === 401) return handleUnauthorized();
                    if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
                    setForm((p) => ({ ...p, user_id: defaultUser || null }));
                    setShowConfig(false);
                  } catch (err) {
                    setError(err.message);
                  }
                }}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {noticeTask && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Aviso a cliente</h5>
                <button className="btn-close" onClick={() => setNoticeTask(null)}></button>
              </div>
              <div className="modal-body">
                <textarea className="form-control" rows="4" value={noticeText} onChange={(e) => setNoticeText(e.target.value)} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => { setNoticeTask(null); updateTaskStatus(noticeTask, 'realizada'); }}>Descartar</button>
                <button className="btn btn-outline-success" disabled={!noticeTask.client_id} onClick={() => { setNoticeTask(null); updateTaskStatus(noticeTask, 'realizada'); }}>Enviar aviso whatsapp</button>
                <button className="btn btn-primary" onClick={() => { setNoticeTask(null); updateTaskStatus(noticeTask, 'realizada'); }}>Enviar aviso email</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Historial tareas realizadas</h5>
                <button className="btn-close" onClick={() => setHistoryOpen(false)}></button>
              </div>
              <div className="modal-body">
                {historyItems.length ? (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Cliente</th>
                          <th>Proyecto</th>
                          <th>Importancia</th>
                          <th>Asignada a</th>
                          <th>Comentarios</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyItems.map((t) => (
                          <tr key={t.id}>
                            <td>{t.titulo}</td>
                            <td>{t.client_name || '-'}</td>
                            <td>{t.project_title || '-'}</td>
                            <td><span className={`badge bg-${t.importance === 'alta' ? 'danger' : t.importance === 'media' ? 'warning text-dark' : 'secondary'}`}>{t.importance}</span></td>
                            <td>{t.user_name || t.user_email || '-'}</td>
                            <td>{t.comentarios || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p>No hay tareas realizadas.</p>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setHistoryOpen(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {timeModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Tiempo dedicado - {timeModal.task?.titulo}</h5>
                <button className="btn-close" onClick={() => setTimeModal({ open: false, task: null, hours: 1, minutes: 0, comments: '', user_id: null, date: new Date().toISOString().slice(0, 10) })}></button>
              </div>
              <div className="modal-body">
                <TimePresets onChange={(hours, minutes) => setTimeModal((p) => ({ ...p, hours, minutes }))} />
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <label className="form-label">Fecha</label>
                    <input
                      type="date"
                      className="form-control"
                      value={timeModal.date}
                      onChange={(e) => setTimeModal((p) => ({ ...p, date: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Horas</label>
                    <input type="number" min="0" className="form-control" value={timeModal.hours} onChange={(e) => setTimeModal((p) => ({ ...p, hours: Number(e.target.value) }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Minutos</label>
                    <input type="number" min="0" max="59" className="form-control" value={timeModal.minutes} onChange={(e) => setTimeModal((p) => ({ ...p, minutes: Number(e.target.value) }))} />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label">Comentarios</label>
                  <textarea className="form-control" rows="3" value={timeModal.comments} onChange={(e) => setTimeModal((p) => ({ ...p, comments: e.target.value }))} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Usuario</label>
                  <Select
                    options={userOptions}
                    value={userOptions.find((o) => o.value === timeModal.user_id) || null}
                    onChange={(opt) => setTimeModal((p) => ({ ...p, user_id: opt ? opt.value : null }))}
                    placeholder="Selecciona usuario"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setTimeModal({ open: false, task: null, hours: 1, minutes: 0, comments: '', user_id: null, date: new Date().toISOString().slice(0, 10) })}>Cerrar</button>
                <button className="btn btn-primary" disabled={savingTime} onClick={async () => {
                  const mins = Number(timeModal.hours) * 60 + Number(timeModal.minutes || 0);
                  if (!mins) return alert('Tiempo debe ser mayor que 0');
                  setSavingTime(true);
                  try {
                    const res = await apiFetch(`/api/tasks/${timeModal.task.id}/time`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ minutes: mins, comments: timeModal.comments, user_id: timeModal.user_id, date: timeModal.date || new Date().toISOString().slice(0, 10) })
                    });
                    const data = await parseJsonSafe(res);
                    if (res.status === 401) return handleUnauthorized();
                    if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
                    alert('Tiempo guardado');
                    setTimeModal({ open: false, task: null, hours: 1, minutes: 0, comments: '', user_id: null, date: new Date().toISOString().slice(0, 10) });
                  } catch (err) {
                    alert(err.message);
                  } finally {
                    setSavingTime(false);
                  }
                }}>{savingTime ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {commentModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Comentarios</h5>
                <button className="btn-close" onClick={() => setCommentModal({ open: false, text: '' })}></button>
              </div>
              <div className="modal-body">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{commentModal.text || 'Sin comentarios'}</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setCommentModal({ open: false, text: '' })}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
